import isEqual from "lodash/isEqual";
import {
  AddressFragment,
  TenantQuery,
  UserAddressInput,
  VerifiedAddressFragment,
  VerifyAddressInput,
} from "services/graphql/generated";
import { AddressDetailsFormValues } from "components/Forms/AddressDetailsForm";
import { AutocompletedAddressValues } from "@pepdirect/shared/types";
import { VerificationAddrTypes } from "components/Modals/VerificationAndConfirmationModal/AddressVerificationModal";

// returns first address with matching id from array of address objects
export const getAddressById = (
  addresses: AddressFragment[] | null,
  id: string
): AddressFragment | null => {
  if (!addresses) return null;

  for (const addr of addresses) {
    if (addr.id === id) return addr;
  }

  return null;
};

export const isEnteredAndVerifiedAddressSame = (
  enteredAddr: VerifyAddressInput,
  verifiedAddr: VerifiedAddressFragment
): boolean => {
  // line2 is optional, so we get a false negative if we check null === "" etc.
  // so we only check equality if both fields exist, otherwise both fields DON'T
  // exist which means they are equal
  const isLine2Equal =
    (enteredAddr.line2 &&
      verifiedAddr.line2 &&
      enteredAddr.line2?.toLowerCase() === verifiedAddr.line2?.toLowerCase()) ||
    true;

  return (
    enteredAddr.line1?.toLowerCase() === verifiedAddr.line1?.toLowerCase() &&
    isLine2Equal &&
    enteredAddr.city?.toLowerCase() === verifiedAddr.city?.toLowerCase() &&
    enteredAddr.state?.toLowerCase() === verifiedAddr.state?.toLowerCase() &&
    enteredAddr.zip?.toLowerCase() === verifiedAddr.zip?.toLowerCase() &&
    enteredAddr.countryCode?.toLowerCase() ===
      verifiedAddr.countryCode?.toLowerCase()
  );
};

// convert form values to AddressVerificationInput
// type for address verification
export const formatShipToAddrVerifInput = (
  ship: AddressDetailsFormValues
): VerifyAddressInput => {
  return {
    city: ship.city,
    company: ship.company || null,
    countryCode: ship.country?.value,
    firstName: ship.firstName,
    lastName: ship.lastName,
    line1: ship.line1,
    line2: ship.line2 || null,
    state: ship.state?.value,
    zip: ship.zip,
  };
};

interface getAddressForCreateUpdateUserAddressArgs {
  formValues: AddressDetailsFormValues;
  confirmedAddress: VerificationAddrTypes;
  verifiedAddress?: VerifiedAddressFragment;
}

// add form details that aren't part of address verification
// to the confirmed address to create the final user address to save
export const getUserAddressInputFromVerification = ({
  formValues,
  confirmedAddress,
  verifiedAddress,
}: getAddressForCreateUpdateUserAddressArgs): UserAddressInput => {
  const {
    city,
    company,
    countryCode,
    firstName,
    lastName,
    line1,
    line2,
    state,
    zip,
  } = confirmedAddress;
  const { isDefault, nickname, phoneNumber, specialInstructions } = formValues;
  const validated = confirmedAddress === verifiedAddress;

  return {
    city,
    company,
    countryCode,
    firstName,
    lastName,
    line1,
    line2,
    state,
    zip,
    isDefault,
    nickname,
    phoneNumber,
    specialInstructions,
    validated,
  };
};

// Convert form values to UserAddressInput needed for create/update user address.
export const getVerifiedUserAddressInputFromFormValues = (
  formValues: AddressDetailsFormValues
): UserAddressInput => {
  const {
    firstName,
    lastName,
    company,
    line1,
    line2,
    city,
    state,
    country,
    zip,
    phoneNumber,
    nickname,
    specialInstructions,
    isDefault,
  } = formValues;

  return {
    firstName,
    lastName,
    company,
    line1,
    line2,
    city,
    zip,
    phoneNumber,
    nickname,
    specialInstructions,
    isDefault,
    state: state.value,
    countryCode: country.value,
    validated: true,
  };
};

interface IsShipVerifiedArgs {
  tenant: TenantQuery["tenant"];
  shipSelectedFromAutocomplete: AutocompletedAddressValues | null;
  newShip?: Partial<AddressDetailsFormValues>;
  savedShip?: AddressFragment | null;
}

export const determineIfShipVerified = ({
  tenant,
  shipSelectedFromAutocomplete,
  newShip,
  savedShip,
}: IsShipVerifiedArgs) => {
  const isValidationOptional =
    tenant?.contactConfig.shippingAddressConfig.requireValidation ===
    "OPTIONAL";

  // a saved ship is already verified if validation is optional
  // if validation NOT optional, we want to run verification on the saved ship
  if (savedShip) return isValidationOptional;

  // PCS-1014 only verify ship with Loqate once (don't Find/Retrieve then Verify)
  // if a user chooses an autocompleted address and doesn't change it,
  // it has already been verified by the Loqate Find and Retrieve APIs
  if (shipSelectedFromAutocomplete && newShip) {
    const { line1, line2, city, state, zip, country } = newShip;
    const formattedNewShip: AutocompletedAddressValues = {
      line1: line1 || "",
      line2: line2 || "",
      city: city || "",
      state: state?.value || "",
      zip: zip || "",
      country: country?.value || "",
    };
    if (isEqual(shipSelectedFromAutocomplete, formattedNewShip)) return true;
  }

  return false;
};
